import React, { useState, useEffect } from "react";
import { useMatch, useNavigate, useParams } from "react-router";
import TaskComponent from "@/components/tasks/Task";
import { Task, Role, Project, TaskPrompt } from "@/api/types";
import SimplePage from "@/components/structure/SimplePage";
import { apiClient } from "@/api";
import { PromptType, TaskPromptProps } from "@/components/tasks/types";
import { useAuth } from "@/contexts/AuthContext";
import EditableTaskComponent from "@/components/tasks/editing/EditableTask";
import AppButton from "@/components/structure/AppButton";
import AppLinkButton from "@/components/structure/AppLinkButton";

// help content
import editTaskHelp from "@/assets/helpdocs/edit-task.md"  with { type: "text" };

export default function DoTask() {
  const { isAuthenticated } = useAuth();
  const editMatch = useMatch("/tasks/:taskId/edit");
  const isEdit = Boolean(editMatch);
  const { taskId } = useParams<{ taskId: string }>();
  const [taskData, setTaskData] = useState<Task | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isDirty, setIsDirty] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTask = async () => {
      try {
        const data = await apiClient.get<Task>(`/tasks/${taskId}/`);

        setTaskData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      fetchTask();
    }
  }, [isAuthenticated, taskId]);

  useEffect(() => {
    const fetchProject = async (projectId: string) => {
      try {
        const data = await apiClient.get<Project>(`/projects/${projectId}/`);
        setProject(data);
      } catch (err) {
        console.error("Error fetching project:", err);
      }
    };

    if (taskData?.project) {
      fetchProject(taskData.project);
    }
  }, [taskData?.project]);

  let contents = null;

  if (loading) {
    contents = <div>Loading task {taskId}...</div>;
  } else if (error) {
    contents = <div>Error: {error}</div>;
  } else if (!taskData) {
    contents = <div>No task data found</div>;
  } else {
    // if we're editing, show the task in edit mode
    contents = isEdit ? (
      <EditableTaskComponent
        id={taskId}
        task={taskData}
        project={project}
        onDirtyChange={(dirty) => setIsDirty(dirty)}
      />
    ) : (
      <TaskComponent
        id={taskId}
        task={taskData}
        project={project}
      />
    );
  }

  // while editing and if the task is dirty, confirm before navigating away
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isEdit && isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEdit, isDirty]);


  const viewTask = () => {
    if (isDirty) {
      if (window.confirm("You have unsaved changes. Are you sure you want to leave?")) {
        navigate(`/tasks/${taskId}`);
      }
    } else {
      navigate(`/tasks/${taskId}`);
    }
  };

  return (
    <SimplePage title="Task Details" helpContent={editTaskHelp} helpTitle="Editing Tasks">
      <div className="flex justify-between mb-4">
        <div className="mb-4">
          <h1 className="text-lg"><strong>Project:</strong> <AppLinkButton to={`/projects/${project?.id}/`} variant="primary">{ project?.name }</AppLinkButton></h1>
          <h1 className="text-lg"><strong>Task:</strong> {taskData && `${taskData.title}` || `#${taskId}`}</h1>
        </div>

        {/* if we're a facilitator for this task, we can show additional controls */}
        {taskData?.roles?.includes(Role.Facilitator) && (
          <div>
            {!isEdit ? (
              <AppButton
                variant="primary"
                onClick={() => navigate(`/tasks/${taskId}/edit`)}
              >
                Edit Task
              </AppButton>
            ) : (
              <AppButton
                variant="primary"
                onClick={viewTask}
              >
                View Task
              </AppButton>
            )}
          </div>
        )}
      </div>

      {/* Render the task component */}
      {contents}
    </SimplePage>
  );
}
